<template>
  <div :class="$style.campaign_page">
    <MoleculesConsumerHeadingWithImage
      title="Campaign"
      sub-title="キャンペーン一覧"
      image-src="/images/site/campaign/campaign_fv.jpg"
      background-color="#eef2f7"
    />
    <div :class="$style.head_message">
      <p :class="$style.title">
        2点以上税込<span :class="$style.price_lg">35,000</span>円以上のお買い上げで<span :class="$style.price_xl"
          >10</span
        >%OFF
        <span :class="$style.description"> ※ 価格が3,500円以下の商品は対象外 </span>
      </p>
    </div>
    <div v-if="pickupItems.length" :class="$style.container">
      <MoleculesConsumerHeading title="期間限定キャンペーン" variant="sub" align="left" />
      <PagesCampaignList :page="pickupItems" />
    </div>
    <div v-if="campaignItems.length" :class="$style.container">
      <MoleculesConsumerHeading title="お得なキャンペーン" variant="sub" align="left" />
      <PagesCampaignList :page="campaignItems" />
    </div>
    <div :class="$style.banner_area">
      <AtomsConsumerLink url="https://lin.ee/x8hj0xN" target="_blank">
        <AtomsConsumerImage
          src="/images/site/campaign/line_banner_pc.png"
          class="page-parts-pc"
          alt="LINEで友だち登録"
        />
        <AtomsConsumerImage
          src="/images/site/campaign/line_banner_sp.png"
          class="page-parts-sp"
          alt="LINEで友だち登録"
        />
      </AtomsConsumerLink>
      <AtomsConsumerLink
        url="https://apay-up-banner.com?merchantId=A25IWXTF7ZJGU6&banner=14_Amazon_Pay_BBP_1000x120.png&locale=ja_JP&utm_source=A25IWXTF7ZJGU6"
        class="page-parts-pc"
        blank
      >
        <AtomsConsumerImage
          src="https://apay-up-banner.com/banner/14_Amazon_Pay_BBP_1000x120.png?merchantId=A25IWXTF7ZJGU6&locale=ja_JP&utm_source=A25IWXTF7ZJGU6"
          :width="1000"
          :height="120"
          :class="$style.amazon_pay_banner_img"
        />
      </AtomsConsumerLink>
      <AtomsConsumerLink
        url="https://apay-up-banner.com?merchantId=A25IWXTF7ZJGU6&banner=02_Amazon_Pay_BBP_710x160.png&locale=ja_JP&utm_source=A25IWXTF7ZJGU6"
        class="page-parts-sp"
        blank
      >
        <AtomsConsumerImage
          src="https://apay-up-banner.com/banner/02_Amazon_Pay_BBP_710x160.png?merchantId=A25IWXTF7ZJGU6&locale=ja_JP&utm_source=A25IWXTF7ZJGU6"
          :class="$style.amazon_pay_banner_img"
          :width="710"
          :height="160"
        />
      </AtomsConsumerLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PageFindFragmentFragment } from "~/types/type.generated"
import { usePublicPageFindQuery } from "~/gql/urql.generated"
import { useCustomMeta } from "~/composables/meta"
import { useToast } from "~/state"

definePageMeta({ layout: "default-with-extended-footer" })

const customMeta = useCustomMeta()
const toast = useToast()

// キャンペーンは特集ほど同時公開される数がないのでページャ機能はなし
const variables = computed(() => {
  return {
    pageType: "campaign",
    page: 1,
    limit: 100,
  }
})
const { executeQuery } = usePublicPageFindQuery({ variables, pause: true })
const { data: resCampaignSearchQuery, error: campaignSearchError } = await useAsyncData(
  async () => (await executeQuery()).data.value?.publicPageFind?.items,
)

const campaignItems = computed<PageFindFragmentFragment[]>(() => {
  if (!resCampaignSearchQuery.value) return []
  return resCampaignSearchQuery.value.filter((item) => !item.campaign_pickup)
})

const pickupItems = computed<PageFindFragmentFragment[]>(() => {
  if (!resCampaignSearchQuery?.value) return []
  return resCampaignSearchQuery.value.filter((item) => item.campaign_pickup)
})

watch(
  () => campaignSearchError.value,
  (value) => {
    if (!value) return
    toast?.showErrorToasted(value.message)
  },
)

useHead(
  customMeta.fetch({
    title: "キャンペーン一覧",
    description: "開催中のお得なキャンペーンや期間限定キャンペーン",
  }),
)
</script>

<style scoped module lang="scss">
.campaign_page {
  display: flex;
  flex-direction: column;
  gap: 3rem;
  .head_message {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    padding: 3rem 1rem;
    .title {
      @include main-font;
      position: relative;
      align-items: center;
      font-size: $font-size-16;
      line-height: 1.5;
      color: $primary;
      border-bottom: 1px solid $primary;
      padding: 1rem 0;
      margin-bottom: 2.75rem;
      @include md {
        white-space: nowrap;
        font-size: $font-size-30;
      }
      .price_lg {
        @include main-font;
        font-size: $font-size-24;
        font-weight: bold;
        padding: 0 0.25rem;
        @include md {
          font-size: $font-size-48;
        }
      }
      .price_xl {
        @include main-font;
        font-size: $font-size-28;
        padding-right: 0.25rem;
        @include md {
          font-size: $font-size-64;
        }
      }
    }
    .description {
      position: absolute;
      bottom: -2.75rem;
      right: 0;
      font-size: $font-size-14;
      color: $black-80;
    }
  }
  .container {
    @include container-lg;
    margin-bottom: 3rem;
  }
  .banner_area {
    @include container-md;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    img {
      width: 100%;
      object-fit: cover;
      border-radius: 0.375rem;
    }
    .amazon_pay_banner_img {
      border-radius: 0;
    }
  }
}
</style>
